import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThunkDispatch } from "@reduxjs/toolkit";
import CustomSwitch from "../../components/CustomSwitch";
import TalentRegistrationPanel from "./components/TalentRegistrationPanel";
import maskEmail from "../../utils/maskEmail";
import { deleteTalent, getListNewTalent, setIsOpenRegisterPanel,
  setSelectedTalent, getOptionsRelatedTalent, setCurrentPageNewList, setEditingTalent,
  setIsLoadingDelete, setIsLoadingSendMail, setIsLoadingAddUpdate, resendEmailApproveTalent,
  approveTalent, cancelTalentApproval, setSearchQuery, setTalentRegisterPageSize, setRegisterChannelFilter, setFilterStatus, approveTalentBulk, cancelApproveTalentBulk, setApprovingStatus, resetBulkApproveState, resetActionState, handleSortByHeader
} from "reducers/companyTalent";
import {useDispatch, useSelector} from "react-redux";
import CompanyHeader from "components/CompanyHeader";
import CompanySideMenu from "components/CompanySideMenu";
import {formatDate, formatDateTime, formatName, trimStringFields, getAgeByYearMonthDay, renderSafeMessage} from "utils/format";
import { useSnackbar } from "notistack";
import CustomToast from "components/CustomToast";
import ConfirmPopup from "components/ConfirmPopup";
import CustomPagination from "components/CustomPagination";
import {LoadingIcon, LoadingSpinner} from "components/Icons";
import ReturnButton from "../../components/ReturnButton";
import CustomButton from "../../components/CustomButton";
import {TALENT_TEMP_STATUS, STAFF_TYPE, TALENT_REGISTER_CHANNEL_OPTIONS} from '../../constants/index';
import Copyright from "../../components/Copyright";
import TextInput from "../../components/TextInput";
import Dropdown from "../../components/Dropdown";
import i18n from 'i18next';

const pageSizeOptions = [
    {
        id: 20,
        name: "20件を表示",
    },
    {
        id: 50,
        name: "50件を表示",
    },
    {
        id: 100,
        name: "100件を表示",
    },
    {
        id: 200,
        name: "200件を表示",
    },
];
let approvalTime = 5 * 60 * 1000 + 5000 ; // 5 minutes + 5 seconds in milliseconds

const getScheduledBulkApprovalTime = (delayTime: number) => {
  const scheduledTime = new Date(Date.now() + delayTime);
  return `${String(scheduledTime.getHours()).padStart(2, '0')}:${String(scheduledTime.getMinutes()).padStart(2, '0')}`;
};

const TalentRegistration = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { fromMenu } = location.state || {};
  const [confirmText, setConfirmText] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [displaySelectedCount, setDisplaySelectedCount] = useState<number>(0);
  const [selectionPage, setSelectionPage] = useState<number | null>(null);
  const [isOnConfirmDelete, setIsOnConfirmDelete] = useState(false);
  const [isOnConfirmBulkApprove, setIsOnConfirmBulkApprove] = useState(false);
  const [scheduledBulkApprovalTime, setScheduledBulkApprovalTime] = useState('');
  const [disableId, setDisableId] = useState(null);
  const [blockTalentIdApproveQuickly, setBlockTalentIdApproveQuickly] = useState<Set<number>>(new Set());
  const [approvingTalentIds, setApprovingTalentIds] = useState<Set<number>>(new Set());
  const approvalCompletionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const approvingTalentTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const { selectedTalent, talentNewList, actionStatus, actionName, message, isOpenRegisterPanel,
    isSuccess, talentOptionsRelatedData, pageSizeTalentRegister, talentNewCount, currentPageNewList, 
    isLoadingDelete, isLoadingSendMail, searchQuery, registerChannelFilter, filterStatus, approvingStatus, fetchTalentNewListLoading, talentRegTableHeaders, newListSortBy, newListSortOrder, loading } = useSelector((state: any) => state.companyTalent);
  const [pageSizeOption, setPageSizeOption] = useState(pageSizeOptions.find((item: any) => item.id == pageSizeTalentRegister));
  const isAnyTalentApproving = approvingTalentIds.size > 0 || approvingStatus;

  const clearTalentSelection = () => {
    setSelectedRows([]);
    setDisplaySelectedCount(0);
    setSelectionPage(null);
  };

  // Mutable refs to prevent stale closure issues in timers
  const filterStatusRef = useRef(filterStatus);
  const registerChannelFilterRef = useRef(registerChannelFilter);
  const currentPageRef = useRef(currentPageNewList);
  const searchQueryRef = useRef(searchQuery);

  useEffect(() => {
    filterStatusRef.current = filterStatus;
  }, [filterStatus]);

  useEffect(() => {
    registerChannelFilterRef.current = registerChannelFilter;
  }, [registerChannelFilter]);

  useEffect(() => {
    currentPageRef.current = currentPageNewList;
  }, [currentPageNewList]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  // Sync selectedTalent with the latest data from talentNewList
  useEffect(() => {
    if (!selectedTalent?.id) return;

    const latestTalent = talentNewList.find((item: any) => item.id === selectedTalent.id);
    if (!latestTalent) return;

    if (selectedTalent.status !== latestTalent.status) {
      dispatch(setSelectedTalent({ ...selectedTalent, ...latestTalent }));
    }
  }, [talentNewList, selectedTalent?.id, selectedTalent?.status, selectedTalent]);

  const parseApproveProcessIds = (value: any) => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map((item: any) => Number(item))
        .filter((item: number) => Number.isFinite(item));
    }

    if (typeof value === "string") {
      const matchedIds = value.match(/\d+/g);
      if (matchedIds) {
        return matchedIds.map((item: string) => Number(item)).filter((item: number) => Number.isFinite(item));
      }
    }

    if (typeof value === "number") {
      return [value];
    }

    return [];
  };

  const handleAdd = () => {
    dispatch(setEditingTalent(true))
    dispatch(setSelectedTalent({}));
    dispatch(setIsOpenRegisterPanel(true));
  };

  const handleEdit = (talent: any) => {
    dispatch(setSelectedTalent(talent));
    dispatch(setIsOpenRegisterPanel(true));
  };

  const handleClose = () => {
    dispatch(setIsOpenRegisterPanel(false));
    dispatch(setSelectedTalent({}));
    dispatch(setIsLoadingAddUpdate(false))
  };

  const handleCloseOnEscKeyDown = (event:any) => {
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.key === "Escape") {
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA")
      ) {
        activeElement.blur();
        return;
      } else {
        handleClose();
      }
    }
  };

  useEffect(() => {
    if (!isOpenRegisterPanel) {
      dispatch(setSelectedTalent({}));
      dispatch(setIsLoadingAddUpdate(false))
    }

    if (isOpenRegisterPanel) {
      window.addEventListener("keydown", handleCloseOnEscKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleCloseOnEscKeyDown);
    };
  }, [isOpenRegisterPanel]);

  useEffect(() => {
    return () => {
      if (approvalCompletionTimeoutRef.current) {
        clearTimeout(approvalCompletionTimeoutRef.current);
      }
      approvingTalentTimeoutsRef.current.forEach((timer) => clearTimeout(timer));
      approvingTalentTimeoutsRef.current.clear();
      clearTalentSelection();
    };
  }, []);

  const getSelectableRows = () => {
    return talentNewList.filter((item: any) => ![TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVED].includes(getTalentStatus(item?.id)) && !approvingTalentIds.has(item?.id));
  };

  const isCheckAllActive = () => {
    const selectableRows = getSelectableRows();
    return selectableRows.length > 0;
  }

  const isAllCurrentPageSelected = () => {
    const selectableRows = getSelectableRows();
    if (selectableRows.length === 0) return false;

    return selectionPage === currentPageNewList && selectableRows.every((item: any) =>
      selectedRows.some((row: any) => row.id === item.id)
    );
  };

  const updateSelectedRowsByIds = (targetIds: number[]) => {
    const matchedRows = talentNewList.filter((item: any) => targetIds.includes(item?.id));
    setSelectedRows(matchedRows);
    setDisplaySelectedCount(targetIds.length);
    setSelectionPage(matchedRows.length > 0 ? currentPageNewList : null);
  };

  const handleSelectAll = () => {
    const selectableRows = getSelectableRows();
    const selectableIds = selectableRows.map((item: any) => item.id);

    let nextSelectedRows = [];

    if (selectionPage === currentPageNewList && selectedRows.length === selectableIds.length) {
      nextSelectedRows = [];
    } else {
      nextSelectedRows = selectableRows;
    }

    setSelectedRows(nextSelectedRows);
    setDisplaySelectedCount(nextSelectedRows.length);
    setSelectionPage(currentPageNewList);
  };

  const handleSelectRow = (item: any) => {
    let status = getTalentStatus(item?.id);
    if (status == TALENT_TEMP_STATUS.REGISTERED) {
      return
    }

    let nextSelectedRows = [...selectedRows];

    if (selectionPage !== currentPageNewList) {
      nextSelectedRows = [];
    }

    const selectedIndex = nextSelectedRows.map((n: any) => n.id).indexOf(item?.id);

    if (selectedIndex === -1) {
      nextSelectedRows = nextSelectedRows.concat(item);
    } else if (selectedIndex === 0) {
      nextSelectedRows = nextSelectedRows.slice(1);
    } else if (selectedIndex === nextSelectedRows.length - 1) {
      nextSelectedRows = nextSelectedRows.slice(0, -1);
    } else if (selectedIndex > 0) {
      nextSelectedRows = [
        ...nextSelectedRows.slice(0, selectedIndex),
        ...nextSelectedRows.slice(selectedIndex + 1),
      ];
    }

    setSelectedRows(nextSelectedRows);
    setDisplaySelectedCount(nextSelectedRows.length);
    setSelectionPage(currentPageNewList);
  };

  useEffect(() => {
    dispatch(getOptionsRelatedTalent(""));
    getListTalent(currentPageNewList, filterStatus);

    return () => {
      dispatch(resetActionState());
    };
  }, []);

  useEffect(() => {
    const approveProcesses = Array.isArray(talentOptionsRelatedData?.talent_approve_process)
      ? talentOptionsRelatedData.talent_approve_process
      : [];

    const nextApprovingTalentIds = new Set<number>();
    let hasBulkApproving = false;

    approveProcesses.forEach((process: any) => {
      if (process?.status !== "approving") return;

      const remainingTime = typeof process?.remaining_time === "number" && process.remaining_time >= 0
        ? process.remaining_time
        : approvalTime;

      if (process?.approve_type === "individual") {
        const ids = parseApproveProcessIds(process?.talent_temp_ids);
        ids.forEach((id: number) => {
          nextApprovingTalentIds.add(id);
          setupIndividualApprovalTimer(id, remainingTime);
        });
      }

      if (process?.approve_type === "bulk") {
        hasBulkApproving = true;
        const bulkIds = parseApproveProcessIds(process?.talent_temp_ids);
        updateSelectedRowsByIds(bulkIds);

        if (approvalCompletionTimeoutRef.current) {
          clearTimeout(approvalCompletionTimeoutRef.current);
        }
        setScheduledBulkApprovalTime(getScheduledBulkApprovalTime(remainingTime));
        approvalCompletionTimeoutRef.current = setTimeout(() => {
          if(staffInfo?.type !== STAFF_TYPE.GENERAL) {
            showToast("メッセージ", i18n.t('MSG_TALENT_APPROVAL_COMPLETED'), "success");
          }
          getListTalent(); // Dynamically reads latest ref values
          clearTalentSelection();
          setScheduledBulkApprovalTime('');
          dispatch(resetBulkApproveState());
        }, remainingTime);
      }
    });

    setApprovingTalentIds(nextApprovingTalentIds);
    dispatch(setApprovingStatus(hasBulkApproving));
    if (!hasBulkApproving) {
      setScheduledBulkApprovalTime('');
    }
  }, [talentOptionsRelatedData]);

  useEffect(() => {
    const approveProcesses = Array.isArray(talentOptionsRelatedData?.talent_approve_process)
      ? talentOptionsRelatedData.talent_approve_process
      : [];

    const bulkProcess = approveProcesses.find(
      (process: any) => process?.status === "approving" && process?.approve_type === "bulk"
    );

    if (bulkProcess && talentNewList?.length > 0 && approvingStatus) {
      const bulkIds = parseApproveProcessIds(bulkProcess.talent_temp_ids);
      const matchedRows = talentNewList.filter((item: any) => bulkIds.includes(item?.id));
      setSelectedRows(matchedRows);
      setDisplaySelectedCount(bulkIds.length);
      setSelectionPage(matchedRows.length > 0 ? currentPageNewList : null);
    } else if (!bulkProcess && approvingStatus) {
      dispatch(setApprovingStatus(false));
      clearTalentSelection();
      if (approvalCompletionTimeoutRef.current) {
        clearTimeout(approvalCompletionTimeoutRef.current);
        approvalCompletionTimeoutRef.current = null;
      }
    }
  }, [talentNewList, talentOptionsRelatedData, currentPageNewList]);


  const showToast = (title: any, mes: any, variant: any) => {
    enqueueSnackbar("I love snacks.", {
      autoHideDuration: 7000,
      content: (key) => (
        <CustomToast title={title} message={renderSafeMessage(mes)} variant={variant}
          snackbarKey={key} closeSnackbar={closeSnackbar} />
      ),
    });
  };

  useEffect(() => {
      if (actionStatus === 'fulfilled') {
        let title = "メッセージ";

        switch(actionName) {
          case 'add':
            showToast(title, message, isSuccess ? "success" : "error");
            getListTalent(1, filterStatusRef.current);
            dispatch(setIsLoadingAddUpdate(false))
            break;
          case 'update':
            showToast(title, message, isSuccess ? "success" : "error");
            getListTalent(currentPageRef.current, filterStatusRef.current);
            dispatch(setIsLoadingAddUpdate(false))
            clearTalentSelection();
            break;
          case 'delete':
            showToast(title, message, isSuccess ? "success" : "error");
            getListTalent(currentPageRef.current, filterStatusRef.current);
            dispatch(setIsLoadingDelete(false))
            cancelDelete();
            clearTalentSelection();
            break;
          case 'resendEmailApproveTalent':
            if (isSuccess) {
              showToast(title, message, isSuccess ? "success" : "error");
              if(!approvingStatus) {
                clearTalentSelection();
              }
            } else {
              showToast("メッセージ", message, "error");
            }
            setDisableId(null);
            break;
          case 'approveTalent':
              if (isSuccess) {
                showToast(title, message, isSuccess ? "success" : "error");
              } else {
                showToast("メッセージ", message, "error");
              }
              setDisableId(null);
              break;
          case 'cancelTalentApproval':
              if (isSuccess) {
                if(!approvingStatus) {
                  clearTalentSelection();
                }
                dispatch(getOptionsRelatedTalent(""));
                showToast(title, message, isSuccess ? "success" : "error");
              } else {
                showToast("メッセージ", message, "error");
              }
              break;
          case 'approveTalentBulk':
            if (isSuccess) {
              showToast(title, message, isSuccess ? "success" : "error");
              cancelBulkApprove();
              clearTalentSelection();
              getListTalent();
              dispatch(getOptionsRelatedTalent(""));
              
              if (approvalCompletionTimeoutRef.current) {
                clearTimeout(approvalCompletionTimeoutRef.current);
              }
              approvalCompletionTimeoutRef.current = setTimeout(() => {
                if(staffInfo?.type !== STAFF_TYPE.GENERAL) {
                  showToast("メッセージ", i18n.t('MSG_TALENT_APPROVAL_COMPLETED'), "success");
                }
                getListTalent(); // Uses latest refs
                clearTalentSelection();
                dispatch(resetBulkApproveState());
                setScheduledBulkApprovalTime('');
              }, approvalTime);
            } else {
              handleBulkApproveFailure(message);
            }
            break;
          case 'cancelApproveTalentBulk':
            if (isSuccess) {
              approvingTalentTimeoutsRef.current.forEach((timer) => clearTimeout(timer));
              approvingTalentTimeoutsRef.current.clear();
              setApprovingTalentIds(new Set());
              getListTalent();
              dispatch(getOptionsRelatedTalent(""));
              showToast(title, message, isSuccess ? "success" : "error");
              clearTalentSelection();
            } else {
              showToast("メッセージ", message, "error");
            }
            break;
          default:
            break;
        }
      } else if (actionStatus === 'rejected') {
        if (actionName === 'approveTalentBulk') {
          const errMsg = message || i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED');
          handleBulkApproveFailure(errMsg);
        }
      }
  }, [actionStatus, actionName]);

  // Ref-safe getListTalent implementation
  const getListTalent = (
    page = currentPageRef.current,
    status = filterStatusRef.current,
    search = searchQueryRef.current,
    pageSize = pageSizeTalentRegister,
    registerChannel = registerChannelFilterRef.current
  ) => {
    let data = { 
      sortBy: newListSortBy, 
      sortOrder: newListSortOrder, 
      status: status, 
      page: page, 
      pageSize: pageSize || pageSizeTalentRegister, 
      searchQuery: search, 
      registerChannel: registerChannel 
    };
    data = trimStringFields(data);
    dispatch(getListNewTalent(data));
  };

  const getTalentStatus = (id: any) => {
    for (let talent of talentNewList) {
      if (talent.id == id) {
        return talent.status
      }
    }
    return null;
  };

  const handleDelete = () => {
    if(selectedRows.length === 0) return;
    setIsOnConfirmDelete(true)
    setConfirmText(<><p>選択した{selectedRows.length}件のタレントを削除します。<br />よろしいですか？</p></>)
  };

  const confirmDelete = () => {
    if(isLoadingDelete || selectedRows.length === 0) return;
    dispatch(setIsOpenRegisterPanel(false));
    dispatch(setIsLoadingDelete(true));
    let data = {requestBody: {ids: selectedRows.map(item => item.id)}};
    dispatch(deleteTalent(data));
  }

  const cancelDelete = () => {
    setConfirmText(null);
    setIsOnConfirmDelete(false);
  };

  const handleBulkApprove = () => {
    if (selectedRows.length === 0) return;
    setScheduledBulkApprovalTime(getScheduledBulkApprovalTime(approvalTime));
    setIsOnConfirmBulkApprove(true);
    setConfirmText(<><p>{selectedRows.length}件を承認します。この操作を行うと、タレントにメールが送信されます。</p></>);
  };

  const confirmBulkApprove = () => {
    if (approvingStatus) return;
    dispatch(setIsOpenRegisterPanel(false));
    const talentIds = selectedRows.map((item: any) => item.id);
    const data = { requestBody: { ids: talentIds } };
    dispatch(approveTalentBulk(data));
  };

  const cancelBulkApprove = () => {
    setConfirmText(null);
    setIsOnConfirmBulkApprove(false);
  };

  const handleBulkApproveFailure = (errMsg?: string) => {
    if (errMsg) showToast("メッセージ", errMsg, "error");
    cancelBulkApprove();
    setScheduledBulkApprovalTime('');
    dispatch(resetBulkApproveState());
  };

  const handleCancelBulkApproveProcess = () => {
    setIsOnConfirmBulkApprove(true);
    setConfirmText(<><p>一括承認を中止します。よろしいですか？</p></>);
  };

  const confirmCancelBulkApprove = () => {
    if (approvalCompletionTimeoutRef.current) {
      clearTimeout(approvalCompletionTimeoutRef.current);
    }
    dispatch(cancelApproveTalentBulk());
    cancelBulkApprove();
  };

  const handleChangeFilterStatus = (checked: boolean) => {
    dispatch(setFilterStatus(checked));
    dispatch(setCurrentPageNewList(1));
    clearTalentSelection();
  };

  const handleSort = (sortHeader: any) => {
    dispatch(handleSortByHeader(sortHeader));
    clearTalentSelection();
  };

  useEffect(()=> {
    getListTalent(currentPageNewList, filterStatus);
  }, [filterStatus, registerChannelFilter, newListSortBy, newListSortOrder]);

  const getNameByValue = (value: any, items: any) => {
    if (!value || !Array.isArray(items)) return null;
    return items.find((item: any) => item.id == value)?.name ?? '';
  };

  const onPageChange = (pageNumber: any, pageSizeInput: any) => {
    dispatch(setCurrentPageNewList(pageNumber));
    getListTalent(pageNumber, filterStatus);
  };

  const isCurrentPageSelection = selectionPage === currentPageNewList;

  const goToImportCSV = () => {
    dispatch(setRegisterChannelFilter(null));
    dispatch(setIsOpenRegisterPanel(false));
    navigate('/company/talent/import');
  };

  const goToListTalent = () => {
    dispatch(setRegisterChannelFilter(null));
    dispatch(setSelectedTalent({}));
    dispatch(setIsOpenRegisterPanel(false));
    navigate('/company/talent', { state: { isFromDetail: true } });
  };

  const clearApprovingTalentTimeout = (talentId: number) => {
    const timer = approvingTalentTimeoutsRef.current.get(talentId);
    if (timer) {
      clearTimeout(timer);
      approvingTalentTimeoutsRef.current.delete(talentId);
    }
  };

  const setupIndividualApprovalTimer = (talentId: number, delayTime: number = approvalTime) => {
    clearApprovingTalentTimeout(talentId);

    const timer = setTimeout(() => {
      clearApprovingTalentTimeout(talentId);
      setApprovingTalentIds((prev) => {
        const next = new Set(prev);
        next.delete(talentId);
        return next;
      });
      setDisableId(null);
      if(staffInfo?.type !== STAFF_TYPE.GENERAL) {
        showToast("メッセージ", i18n.t('MSG_TALENT_APPROVAL_COMPLETED'), "success");
      }
      
      // Calls getListTalent reading current dynamic values from refs
      getListTalent();
    }, delayTime);

    approvingTalentTimeoutsRef.current.set(talentId, timer);
  };

  const handleIndividualApprovalFailure = (talentId: number) => {
    clearApprovingTalentTimeout(talentId);
    setApprovingTalentIds((prev) => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    setBlockTalentIdApproveQuickly((prev) => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    setDisableId(null);
  };

  const handleClickSendMailOrApprove = async (talent: any) => {
    if (talent?.status == TALENT_TEMP_STATUS.REGISTERED || talent?.status == TALENT_TEMP_STATUS.APPROVED) {
      setDisableId(talent?.id)
      dispatch(resendEmailApproveTalent({ id: talent?.id }));
      return
    }

    if(talent?.status == TALENT_TEMP_STATUS.APPROVING) {
      if (approvingTalentIds.has(talent?.id)) {
        clearApprovingTalentTimeout(talent.id);
        setApprovingTalentIds((prev) => {
          const next = new Set(prev);
          next.delete(talent.id);
          return next;
        });
      }
      setDisableId(null);
      try {
        await dispatch(cancelTalentApproval({ id: talent?.id })).unwrap();
      } catch (error: any) {
        showToast("メッセージ", error?.message || "承認の中止に失敗しました", "error");
      }
      return;
    }

    if (blockTalentIdApproveQuickly?.has(talent?.id)) return;
    setBlockTalentIdApproveQuickly(prev => new Set([...prev, talent.id]));
    setDisableId(talent?.id);

    try {
      const res: any = await dispatch(approveTalent({ id: talent?.id })).unwrap();
      if (res && res.success === false) {
        handleIndividualApprovalFailure(talent.id);
      } else {
        setApprovingTalentIds((prev) => {
          const next = new Set(prev);
          next.add(talent.id);
          return next;
        });
        setupIndividualApprovalTimer(talent.id, approvalTime);
        setSelectedRows((prev) => {
          const next = prev.filter((row: any) => row.id !== talent?.id);
          setDisplaySelectedCount(next.length);
          if (next.length === 0) {
            setSelectionPage(null);
          }
          return next;
        });
        setDisableId(null);
      }
    } catch (error: any) {
      handleIndividualApprovalFailure(talent.id);
      showToast("メッセージ", error?.message || "承認に失敗しました", "error");
    }
  };

  useEffect(() => {
    if (blockTalentIdApproveQuickly && blockTalentIdApproveQuickly.size > 0) {
      setBlockTalentIdApproveQuickly(new Set());
    }
  }, [talentNewList]);

  const handleSetSearchQuery = (value: string) => {
    dispatch(setSearchQuery(value));
  };

  const handleSearchTalent = (event: any) => {
    if (event.key === "Enter") {
      clearTalentSelection();
      dispatch(setCurrentPageNewList(1));
      getListTalent(1, filterStatus);
    }
  };

  const handleClearSearch = () => {
    clearTalentSelection();
    dispatch(setCurrentPageNewList(1));
    handleSetSearchQuery("");
    getListTalent(1, filterStatus, "");
  }

  const handleSetPageSize = (option: any) => {
    clearTalentSelection();
    dispatch(setCurrentPageNewList(1));
    setPageSizeOption(option);
    dispatch(setTalentRegisterPageSize(option.id));
    getListTalent(1, filterStatus, searchQuery, option.id);
  }

  const handleRegisterChannelChange = (option: any) => {
    dispatch(setRegisterChannelFilter(option?.value || null));
    dispatch(setCurrentPageNewList(1));
    clearTalentSelection();
  }
  
  return (
    <>
      <CompanyHeader />
      <div className="mt-6 px-6 flex gap-6 relative">
        <CompanySideMenu />
        <div className="flex-1">
          <div className="mb-4 flex items-center gap-4">
            {!fromMenu && <ReturnButton onClick={() => goToListTalent()}/>}
            {!fromMenu && <span className="text-sm alu-text-base-1">/</span>}
            <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">タレント登録申請一覧</h3>
          </div>
          <div className="mb-6 text-sm">
            <p className="alu-text-base-1">タレントからの登録申請を確認することができます。承認をするとタレント宛にログイン案内のメールが送付されます。</p>
            {staffInfo?.type !== STAFF_TYPE.GENERAL && <p className="alu-text-error">承認するタレントに間違いのないことを必ずご確認ください。</p>}
          </div>
          <div className='mb-6 flex items-center justify-between gap-6'>
            <div className='flex-1 flex items-center gap-4'>
              <div className='w-[10rem]'>
                <Dropdown
                  inputWidth="full"
                  options={pageSizeOptions}
                  placeholder=""
                  value={pageSizeOption}
                  onChange={handleSetPageSize}
                  customStyle='!font-bold'
                />
              </div>
            </div>
            {talentNewCount > pageSizeTalentRegister && (
                <CustomPagination total={talentNewCount} currentPage={currentPageNewList}
                  pageSize={pageSizeTalentRegister} onPageChange={onPageChange}/>
            )}
            <div className='flex-1 flex items-center justify-end'>
              <TextInput
                value={searchQuery}
                onChange={(e: any) => handleSetSearchQuery(e.target.value)}
                onKeyDown={(e: any) => handleSearchTalent(e)}
                onClearQuery={() => handleClearSearch()}
                placeholder="検索"
                searchIcon={true}
                customClassName="!h-[36px] !w-[234px] !text-sm"
              />
            </div>
          </div>
          {approvingStatus && <div className={`mb-6 flex items-center justify-between gap-3 px-4 py-[0.375rem] rounded bg-[#F9F5E6] border border-[#9B5F05]`}>
            <div className="flex items-center gap-3">
              <LoadingSpinner fillColor={"#9B5F05"} />
              <p className="text-base text-[#9B5F05]">承認しました。メールを{scheduledBulkApprovalTime} に送付予定です。</p>
            </div>
            <CustomButton
              disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
              variant="text"
              size="sm"
              onClick={handleCancelBulkApproveProcess}
            >
              一括で承認を取り消す
            </CustomButton>
          </div>}
          <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
            <div className="flex items-center">
              <div className="flex items-center gap-4 flex-1">
                <CustomButton
                  size="sm"
                  variant={approvingStatus ? "outline" : "default"}
                  disabled={selectedRows.length < 1 || approvingStatus || staffInfo?.type == STAFF_TYPE.GENERAL || !isCurrentPageSelection}
                  onClick={handleBulkApprove}
                  customStyle="!min-w-[9rem]"
                >
                  {approvingStatus ? (
                    <>
                      <span className="flex items-center gap-2">
                        <span>承認中...</span>
                        <LoadingSpinner fillColor={"#BBB"} />
                      </span>
                    </>
                  ) : (
                    `${displaySelectedCount ? `${displaySelectedCount}件を` : ""}一括承認する`
                  )}
                </CustomButton>
                <span className="text-[#E0E0E0] ml-4">|</span>
                <CustomButton
                  variant="text"
                  disabled={selectedRows.length < 1 || staffInfo?.type == STAFF_TYPE.GENERAL || approvingStatus || !isCurrentPageSelection} 
                  onClick={handleDelete}>
                  削除
                </CustomButton>
                <div className="flex gap-4 items-center">
                  <span className="text-[#E0E0E0]">|</span>
                  <p onClick={() => dispatch(setFilterStatus(!filterStatus))}
                    className="text-sm alu-text-base-2 cursor-pointer">「新規登録」のみ表示</p>
                  <CustomSwitch 
                    checked={filterStatus}
                    onChange={(e: any) => handleChangeFilterStatus(e.target.checked)}/>
                  <div className="ml-4 w-[14.4375rem]">
                    <Dropdown
                      clearable={true}
                      inputWidth="full"
                      size="filter"
                      customStyle="h-[1.75rem]"
                      options={TALENT_REGISTER_CHANNEL_OPTIONS}
                      value={TALENT_REGISTER_CHANNEL_OPTIONS.find((item: any) => item.value === registerChannelFilter) || null}
                      onChange={handleRegisterChannelChange}
                      placeholder="（登録経路の絞り込み）"
                    />
                  </div>
                </div>
              </div>
              <CustomButton
                onClick={() => navigate("/company/talent/import")}
                variant="outline"
                size="sm"
                disabled={approvingStatus || staffInfo?.type == STAFF_TYPE.GENERAL}
                customStyle="shrink-0"
              >
                CSVで一括アップロード
              </CustomButton>
            </div>
          </div>
          <div className="bg-white rounded-b-lg mb-4 relative">
            <table className="table-auto w-full">
              <thead className="border-b alu-border px-4 py-2">
              <tr>
                <th className="px-4 py-2 text-left w-[60px]">
                  <div className="flex items-center justify-center">
                  <button onClick={handleSelectAll} disabled={staffInfo?.type == STAFF_TYPE.GENERAL || approvingStatus || !isCheckAllActive()}>
                    {isAllCurrentPageSelected() ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <g clipPath="url(#clip0_8574_236)"><path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill={approvingStatus || !isCheckAllActive() ? "#BBBBBB" : "#FF861A"}/></g>
                        <defs><clipPath id="clip0_8574_236"><rect width="24" height="24" fill="white"/></clipPath></defs>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <g clipPath="url(#clip0_8574_230)"><path d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill={staffInfo?.type == STAFF_TYPE.GENERAL || approvingStatus || !isCheckAllActive() ? "#BBBBBB" : "#808080"}/></g>
                        <defs><clipPath id="clip0_8574_230"><rect width="24" height="24" fill="white"/></clipPath></defs>
                      </svg>
                    )}
                  </button>
                  </div>
                </th>
                {talentRegTableHeaders.map((header, idx) => (
                  <th key={idx} className={`px-[0.375rem] py-2 text-left ${header.width ? `w-[${header.width}px]` : header.minWidth ? `min-w-[${header.minWidth}px]` : header.maxWidth ? `max-w-[${header.maxWidth}px]` : ""}`}>
                    <div onClick={() => header.isSort && handleSort(header)}
                         className={`flex items-center ${header.isSort ? 'hover:cursor-pointer' : ''}`}>
                      <p
                        className={`${newListSortBy === header?.field ? 'font-bold underline underline-offset-4 alu-text-base-2' : 'font-normal alu-text-base-1'} text-sm leading-[1.3125]`}>
                        {header.title}
                      </p>
                      {header.isSort && (
                        <button className={`${header.currentSortState === 1 ? 'rotate-180' : 'rotate-0'}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                               viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_8574_176)">
                              <path d="M7 10L12 15L17 10H7Z" fill="#808080"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_8574_176">
                                <rect width="24" height="24" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </button>)}
                    </div>
                  </th>
                ))}
              </tr>
              </thead>
              <tbody>
              {!fetchTalentNewListLoading ? talentNewList.map((item: any, idx: any) => (
                <tr key={idx} className={`hover:bg-[#FFF3E5] border-t border-t-[#E0E0E0] ${selectedTalent?.id === item?.id ? 'outline outline-1 outline-[#FF861A]' : ''}`}>
                  <td className="w-[3.75rem] px-4 py-4 text-left font-normal text-sm leading-[1.3125]">
                    <div className="flex items-center justify-center">
                    <button onClick={(e:any) => {handleSelectRow(item);e.stopPropagation()}} disabled={[TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVED].includes(item.status) || staffInfo?.type == STAFF_TYPE.GENERAL || approvingStatus || approvingTalentIds.has(item.id)}>
                      {!selectedRows.map(row => row?.id).includes(item.id) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <g clipPath="url(#clip0_8574_230)"><path d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill={[TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVED].includes(item.status) || staffInfo?.type == STAFF_TYPE.GENERAL || approvingStatus || approvingTalentIds.has(item.id) ? "#BBBBBB" : "#808080"}/></g>
                          <defs><clipPath id="clip0_8574_230"><rect width="24" height="24" fill="white"/></clipPath></defs>
                        </svg>
                      ) : (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><g clipPath="url(#clip0_8574_236)"><path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill={approvingStatus ? "#BBBBBB" : "#FF861A"}/></g><defs><clipPath id="clip0_8574_236"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>)}
                    </button>
                    </div>
                  </td>
                  <td onClick={() => handleEdit(item)} className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 max-w-[112px]">
                      <p className="text-sm hover:cursor-pointer text-link-with-hover break-words whitespace-normal">{formatName(item?.first_name, item?.last_name)}{!item.is_active && <span className="ml-1 text-sm alu-text-base-2 font-normal whitespace-nowrap">（退会済み）</span>}</p>
                      <p className="text-[0.6875rem] break-words whitespace-normal">{formatName(item?.talent_register?.first_name_kana, item?.talent_register?.last_name_kana)}</p>
                  </td>
                  <td onClick={() => handleEdit(item)} className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 max-w-[240px] truncate">
                    {item.email}
                  </td>
                    <td onMouseEnter={(e: any) => e.currentTarget.parentElement.classList.remove("hover:bg-[#FFF3E5]")}
                      onMouseLeave={(e: any) => e.currentTarget.parentElement.classList.add("hover:bg-[#FFF3E5]")} className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] w-[112px] whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <CustomButton
                            onClick={() => handleClickSendMailOrApprove(item)} 
                            size="sm" customStyle="min-w-[5.625rem]" 
                            disabled={!item.is_active || (staffInfo?.type == STAFF_TYPE.GENERAL && ![TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVED].includes(item.status)) || disableId == item.id}
                            variant={item.status == TALENT_TEMP_STATUS.APPROVING ? "text" : "outline"}
                          >
                            {[TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVED].includes(item.status) ? 'メールを再送' : item.status == TALENT_TEMP_STATUS.APPROVING ? 
                            <>
                              <span className="flex items-center gap-2 text-xs">
                                <span>承認を取り消す</span>
                              </span>
                            </> : '承認'}
                          </CustomButton>
                        </div>
                  </td>
                  <td
                    className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[90px]">
                    <div className="inline-flex items-center justify-center px-2 py-[0.125rem] rounded-2xl bg-[#E0E0E0]">
                      {[TALENT_TEMP_STATUS.NEW, TALENT_TEMP_STATUS.APPROVING].includes(item.status) ? "新規登録" : "登録済"}
                    </div>
                  </td>
                  <td className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[54px]">
                    {item.talent_register?.gender?.label || 'ー'}
                  </td>
                  <td className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[54px]">
                    {getAgeByYearMonthDay(item.talent_register?.birth_year || item.birth_year, item.talent_register?.birth_month || item.birth_month, item.talent_register?.birth_day || item.birth_day) || 'ー'}
                  </td>
                  <td className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[106px]">
                    {item.talent_register?.employee_number || 'ー'}
                  </td>
                  <td className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[80px]">
                    {item.talent_register?.city || 'ー'}
                  </td>
                  <td className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[68px] uppercase">
                    {item.register_channel || 'ー'}
                  </td>
                  <td
                    className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 max-w-[90px]">
                    {getNameByValue(item.talent_type, talentOptionsRelatedData?.talent_types)}
                  </td>
                  <td
                    className="px-[0.375rem] py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                    {formatDateTime(item.updated_at)}
                  </td>
                </tr>
              ))
              : (<tr>
                  <td colSpan={12} className="py-12 text-center">
                    <div className="flex items-center justify-center w-full">
                      <LoadingIcon />
                    </div>
                  </td>
                </tr>) 
              }
              </tbody>
            </table>
          </div>
          {talentNewCount > pageSizeTalentRegister && !fetchTalentNewListLoading && (<div className="flex items-center justify-center">
            <CustomPagination total={talentNewCount} currentPage={currentPageNewList}
                              pageSize={pageSizeTalentRegister} onPageChange={onPageChange}/>
          </div>)}
          {isOnConfirmDelete && confirmText && (
            <ConfirmPopup open={isOnConfirmDelete} onClose={cancelDelete}
                          confirmContent={confirmText} onConfirm={confirmDelete}
                          isLoading={isLoadingDelete} type="delete"/>
          )}
          {isOnConfirmBulkApprove && confirmText && (
            <ConfirmPopup 
              open={isOnConfirmBulkApprove} 
              onClose={cancelBulkApprove}
              confirmContent={confirmText} 
              onConfirm={approvingStatus ? confirmCancelBulkApprove : confirmBulkApprove}
              confirmText={approvingStatus ? "中止する" : "承認"}
              isLoading={loading}
            />
          )}
        </div>
        {isOpenRegisterPanel && (
          <div className="fixed right-2 top-2 bottom-2 z-[999]"><TalentRegistrationPanel
            onClose={handleClose}/></div>)}
      </div>
      <Copyright view="company" />
    </>
  );
};

export default TalentRegistration;